import React from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useToast } from '@ur-solutions/react-components'
import { RegularModal, DateRangePicker, Select } from '@/components_deprecated'
import { startOfMonth, endOfMonth, format } from 'date-fns'
import type { IdVariable } from '@/types/graphql'
import { EXPORT_FORM_RESPONSES } from '../mutations'
import { FORM_TEMPLATE_QUERY } from '../queries'
import type { FormTemplateQuery } from '../types.graphql'
import { ExportFormat } from '@/modules/exports'

const SUBMITTED = 'created_at'

interface ExportPromptProps {
  formId: string
  onResolve: PromptResolve<boolean>
}

export const ExportPrompt: React.FC<ExportPromptProps> = ({
  formId,
  onResolve,
}) => {
  const translations = useTranslate({
    startDate: 'common.start-date',
    endDate: 'common.end-date',
    format: 'common.format',
    export: 'common.export',
    cancel: 'common.cancel',
    success: 'exports.success-user-exports',
    dateRange: 'common.range',
    dateSubmitted: 'common.date-submitted',
    responsesWhere: 'form.export.responses-where',
    isWithin: 'form.export.is-within',
    fieldExclusionHelp: 'form.export.field-exclusion-help',
  })

  const addToast = useToast()

  const { data } = useQuery<FormTemplateQuery, IdVariable>(
    FORM_TEMPLATE_QUERY,
    {
      fetchPolicy: 'cache-first',
      variables: { id: formId },
    }
  )

  const dateFieldOptions = [
    { value: SUBMITTED, label: translations.dateSubmitted || 'Date submitted' },
    ...(data?.formTemplate.formFields.edges ?? [])
      .map(edge => edge.node)
      .filter(field => field.fieldType === 'DATE')
      .map(field => ({ value: field.id, label: field.name })),
  ]

  const { formValues, formChangeHandler: handler } = useForm({
    values: {
      dateRange: [startOfMonth(new Date()), endOfMonth(new Date())] as [
        Date,
        Date,
      ],
      filterField: SUBMITTED,
      format: ExportFormat.EXCEL,
    },
  })

  const [exportFormResponses, { loading }] = useMutation(
    EXPORT_FORM_RESPONSES,
    {
      onCompleted() {
        addToast(
          'success',
          translations.success || 'Export initiated successfully'
        )
        onResolve(true)
      },
      onError(e) {
        // Can't use onErrorAuto here cause it's technically outside of graph context
        addToast('error', 'Export failed')
        console.error(e)
      },
    }
  )

  const handleSubmit = () => {
    const [startDate, endDate] = formValues.dateRange

    void exportFormResponses({
      variables: {
        formTemplateId: formId,
        dateStart: format(startDate, 'yyyy-MM-dd'),
        dateEnd: format(endDate, 'yyyy-MM-dd'),
        filterFieldId:
          formValues.filterField === SUBMITTED ? null : formValues.filterField,
        exportFormat: formValues.format,
      },
    })
  }

  const formatOptions = [{ value: ExportFormat.EXCEL, label: 'Excel' }]

  const handleFormatChange = (value: ExportFormat | null) => {
    if (value) {
      handler('format')(value)
    }
  }

  const handleFilterFieldChange = (value: string | null) => {
    if (value) {
      handler('filterField')(value)
    }
  }

  const hasDateFields = dateFieldOptions.length > 1
  const isFieldChosen = formValues.filterField !== SUBMITTED

  return (
    <RegularModal
      title="Export Form Responses"
      loading={loading}
      onCancel={() => onResolve(false)}
      onSubmit={handleSubmit}
      submitText={translations.export || 'Export'}
      cancelText={translations.cancel || 'Cancel'}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {hasDateFields ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span>{translations.responsesWhere}</span>
              <Select
                width="14rem"
                options={dateFieldOptions}
                value={formValues.filterField}
                onChange={handleFilterFieldChange}
                height="44px"
              />
              <span>{translations.isWithin}</span>
            </div>

            <DateRangePicker
              value={formValues.dateRange}
              onChange={handler('dateRange')}
              InputWrapperProps={{
                className: '[&_div.date-input]:!w-auto',
              }}
            />

            {isFieldChosen && (
              <p className="text-sm text-muted-foreground">
                {translations.fieldExclusionHelp ||
                  'Only responses that have a date in this field are included.'}
              </p>
            )}
          </div>
        ) : (
          <div>
            <label>{translations.dateRange || 'Date Range'}</label>
            <DateRangePicker
              value={formValues.dateRange}
              onChange={handler('dateRange')}
            />
          </div>
        )}

        <div>
          <label>{translations.format || 'Format'}</label>
          <Select
            options={formatOptions}
            value={formValues.format}
            onChange={handleFormatChange}
            width="fit-content"
          />
        </div>
      </div>
    </RegularModal>
  )
}
